import { ThemedText } from '@/components/themed-text';
import { Button } from '@/components/ui/button';
import { prepareReturnToAppLinks } from '@/lib/auth/prepare-return-to-app-link';
import {
  captureReturnToAppFromUrl,
  clearWebReturnToAppFlow,
} from '@/lib/auth/web-onboarding-return-to-app';
import { Ionicons } from '@expo/vector-icons';
import { router } from 'expo-router';
import React, { useEffect, useState } from 'react';
import { ActivityIndicator, Platform, Pressable, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

type ReturnToAppPromptProps = {
  title: string;
  description: React.ReactNode;
  subtitle?: string;
  testId?: string;
  continueOnWebHref?: string;
  continueOnWebLabel?: string;
};

export function ReturnToAppPrompt({
  title,
  description,
  subtitle = 'Toque no botão abaixo para voltar ao aplicativo.',
  testId = 'return-to-app-open',
  continueOnWebHref,
  continueOnWebLabel = 'Continuar no site',
}: ReturnToAppPromptProps) {
  const insets = useSafeAreaInsets();
  const [openAppUrl, setOpenAppUrl] = useState<string | null>(null);
  const [linkLoading, setLinkLoading] = useState(Platform.OS === 'web');

  useEffect(() => {
    if (Platform.OS === 'web') {
      captureReturnToAppFromUrl();
    }
  }, []);

  useEffect(() => {
    if (Platform.OS !== 'web') return;

    let cancelled = false;
    setLinkLoading(true);

    prepareReturnToAppLinks()
      .then(({ openUrl }) => {
        if (!cancelled) setOpenAppUrl(openUrl);
      })
      .finally(() => {
        if (!cancelled) setLinkLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const openAppButtonLabel = 'Voltar para o aplicativo';

  return (
    <View
      className="flex-1 bg-background px-6"
      style={{ paddingTop: insets.top + 24, paddingBottom: Math.max(insets.bottom, 32) }}
    >
      <View className="mx-auto w-full max-w-md flex-1 items-center justify-center">
        <View className="mb-6 rounded-full bg-primary/10 p-6">
          <Ionicons name="checkmark-circle" size={72} color="#FD5001" />
        </View>

        <ThemedText className="text-center text-2xl font-bold text-foreground">
          {title}
        </ThemedText>

        <ThemedText className="mt-3 text-center text-base leading-relaxed text-foreground/80">
          {description}
        </ThemedText>

        {subtitle ? (
          <ThemedText className="mt-4 text-center text-sm text-foreground/60">
            {subtitle}
          </ThemedText>
        ) : null}

        {Platform.OS === 'web' ? (
          <View className="mt-10 w-full">
            {linkLoading || !openAppUrl ? (
              <View className="h-12 w-full items-center justify-center rounded-lg bg-primary/80">
                <ActivityIndicator size="small" color="white" />
              </View>
            ) : (
              <Pressable
                onPress={() => {
                  clearWebReturnToAppFlow();
                  window.location.href = openAppUrl;
                }}
                style={{
                  display: 'flex' as const,
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                  minHeight: 48,
                  borderRadius: 8,
                  backgroundColor: '#FD5001',
                }}
                testID={testId}
              >
                <ThemedText style={{ color: '#fff', fontWeight: '600', fontSize: 16 }}>
                  {openAppButtonLabel}
                </ThemedText>
              </Pressable>
            )}
            {!linkLoading && !openAppUrl && (
              <ThemedText className="mt-2 text-center text-xs text-red-500">
                Não foi possível preparar o link do app. Atualize a página ou faça login no site.
              </ThemedText>
            )}
          </View>
        ) : (
          <Button className="mt-10 w-full" onPress={() => router.replace('/(tabs)/(provider)/home')} testID={testId}>
            <ThemedText className="font-semibold text-white">{openAppButtonLabel}</ThemedText>
          </Button>
        )}

        {Platform.OS === 'web' && continueOnWebHref ? (
          <Pressable onPress={() => router.replace(continueOnWebHref as any)} className="mt-6 py-2">
            <ThemedText className="text-center text-sm text-foreground/50 underline">
              {continueOnWebLabel}
            </ThemedText>
          </Pressable>
        ) : null}
      </View>
    </View>
  );
}
